const keyPrefix = 'qse'
const setItem = (key: string, value: string) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(`${keyPrefix}-${key}`, value)
  }
}
const getItem = (key: string) => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(`${keyPrefix}-${key}`)
  }

  return null
}

const removeItem = (key: string) => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(`${keyPrefix}-${key}`)
  }
}

const setCookie = (key: string, value: string) => {
  if (typeof window !== 'undefined') {
    document.cookie = `${keyPrefix}-${key}=${value}`
  }
}

const getCookie = (key: string) => {
  if (typeof window !== 'undefined') {
    return document.cookie
      .split('; ')
      .find(row => row.startsWith(`${keyPrefix}-${key}=`))
      ?.split('=')[1]
  }

  return null
}

export const storage = {
  localStorage: {
    setItem,
    getItem,
    removeItem,
  },
  cookie: {
    setItem: setCookie,
    getItem: getCookie,
  },
}

export const formatFileSize = (size?: number): string => {
  if (!size || isNaN(size)) return '-'
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`

  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

/**
 * Convert bytes to different units
 * @param bytes - Number of bytes to convert
 * @param unit - Target unit ('B', 'KB', 'MB', 'GB', 'TB')
 * @param decimals - Number of decimal places (default: 2)
 * @returns Converted value in the specified unit
 */
export const convertBytes = (
  bytes: number,
  unit: 'B' | 'KB' | 'MB' | 'GB' | 'TB',
  decimals: number = 2,
): number => {
  if (!bytes || isNaN(bytes)) return 0

  const units = {
    B: 1,
    KB: 1024,
    MB: 1024 * 1024,
    GB: 1024 * 1024 * 1024,
    TB: 1024 * 1024 * 1024 * 1024,
  }

  const divisor = units[unit]
  const result = bytes / divisor

  return Number(result.toFixed(decimals))
}

/**
 * Convert bytes to gigabytes
 * @param bytes - Number of bytes to convert
 * @param decimals - Number of decimal places (default: 2)
 * @returns Converted value in gigabytes
 */
export const bytesToGB = (bytes: number, decimals: number = 2): number => {
  return convertBytes(bytes, 'GB', decimals)
}

/**
 * Convert bytes to megabytes
 * @param bytes - Number of bytes to convert
 * @param decimals - Number of decimal places (default: 2)
 * @returns Converted value in megabytes
 */
export const bytesToMB = (bytes: number, decimals: number = 2): number => {
  return convertBytes(bytes, 'MB', decimals)
}

/**
 * Convert bytes to kilobytes
 * @param bytes - Number of bytes to convert
 * @param decimals - Number of decimal places (default: 2)
 * @returns Converted value in kilobytes
 */
export const bytesToKB = (bytes: number, decimals: number = 2): number => {
  return convertBytes(bytes, 'KB', decimals)
}

/**
 * Determines the appropriate storage unit (KB, MB, GB) for a given byte value
 * @param bytes - Number of bytes
 * @returns Object with unit and formatted value
 */
type StorageUnit = 'KB' | 'MB' | 'GB'

interface FormattedStorageValue {
  value: number
  unit: StorageUnit
  formatted: string
}

/**
 * Formats a single byte value to appropriate unit (KB/MB/GB) with 2 decimal places
 * @param bytes - Number of bytes
 * @returns Formatted string like "X.XX KB", "Y.YY MB", or "Z.ZZ GB"
 */
export const formatStorageValue = (bytes: number): string => {
  if (!bytes || isNaN(bytes) || bytes < 0) {
    return '0.00 MB'
  }

  const MB_THRESHOLD = 1024 * 1024 // 1 MB
  const GB_THRESHOLD = 1024 * 1024 * 1024 // 1 GB

  let value: number
  let unit: StorageUnit

  if (bytes < GB_THRESHOLD) {
    // Less than 1 GB, use MB
    value = bytes / MB_THRESHOLD
    unit = 'MB'
  } else {
    // 1 GB or more, use GB
    value = bytes / GB_THRESHOLD
    unit = 'GB'
  }

  return `${value.toFixed(2)} ${unit}`
}

const formatBytesToUnit = (bytes: number): FormattedStorageValue => {
  const formatted = formatStorageValue(bytes)
  const match = formatted.match(/^([\d.]+)\s+(KB|MB|GB)/)

  if (!match) {
    return { value: 0, unit: 'KB', formatted: '0.00 KB' }
  }

  return {
    value: parseFloat(match[1]),
    unit: match[2] as StorageUnit,
    formatted,
  }
}

/**
 * Formats storage usage with reserved storage and quota
 * Displays used, reserved, and quota in appropriate units (KB/MB/GB) based on size
 * @param usedBytes - Storage used in bytes
 * @param quotaBytes - Storage quota in bytes (displayed with dynamic units: KB/MB/GB)
 * @param reservedBytes - Reserved storage in bytes (optional, shown with + prefix)
 * @returns Formatted string like "X.XX KB (+Y.YY KB Reserved) / Z.ZZ GB"
 */
export const formatStorageUsageWithReserved = (
  usedBytes: number,
  quotaBytes: number,
  reservedBytes?: number,
): string => {
  const GB = 1024 * 1024 * 1024
  const MB = 1024 * 1024

  // Single unit for used + quota, derived from quota size
  const unit: 'GB' | 'MB' | 'KB' = quotaBytes >= GB ? 'GB' : quotaBytes >= MB ? 'MB' : 'KB'

  const fmtShared = (bytes: number) => `${convertBytes(bytes, unit).toFixed(2)} ${unit}`

  if (reservedBytes && reservedBytes > 0) {
    // Reserved keeps its own independent dynamic unit (KB/MB/GB)
    const reserved = formatBytesToUnit(reservedBytes)

    return `${fmtShared(usedBytes)} (+${reserved.formatted} Reserved) / ${fmtShared(quotaBytes)}`
  }

  return `${fmtShared(usedBytes)} / ${fmtShared(quotaBytes)}`
}

/**
 * Formats storage usage without reserved storage
 * Displays used and quota in appropriate units (KB/MB/GB) based on size
 * @param usedBytes - Storage used in bytes
 * @param quotaBytes - Storage quota in bytes (displayed with dynamic units: KB/MB/GB)
 * @returns Formatted string like "X.XX KB / Z.ZZ GB"
 */
export const formatStorageUsage = (usedBytes: number, quotaBytes: number): string => {
  const GB = 1024 * 1024 * 1024
  const MB = 1024 * 1024

  const unit: 'GB' | 'MB' | 'KB' = quotaBytes >= GB ? 'GB' : quotaBytes >= MB ? 'MB' : 'KB'

  const fmt = (bytes: number) => `${convertBytes(bytes, unit).toFixed(2)} ${unit}`

  return `${fmt(usedBytes)} / ${fmt(quotaBytes)}`
}
